import type { GetStaticPathsResult } from 'next';
import { ConfigProvider } from 'shared/config/ConfigContext';
import carrentalData from 'shared/seo/data/carrental.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { DestinationPage } from '@/components/DestinationPage';
import { carRentalSearchFactory, cityLevel1Factory } from '@/components/factory';
import { feConfig } from '@/config';
import { carrentalCities, carrentalCountries, carrentalMapping, getCountriesForDestination } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageProps, SeoPageContext } from '@/interfaces/pages';

export const getStaticPaths = async (): Promise<GetStaticPathsResult> => {
    const destinations = carrentalCities.map((place) => ({
        params: { cityLevel1: place.cityPath, countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
};

export const getStaticProps = async (context: SeoPageContext) => {
    const destination = carrentalCities.find((place) => place.cityPath === context.params.cityLevel1.toLowerCase());
    const destinationCountry = carrentalCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = (carrentalMapping[destination.id] ?? []).slice(0, 11);
    const popularCountries = getCountriesForDestination(destinationCountry, carrentalCountries);

    return {
        props: {
            destination,
            popularCities,
            popularCountries,
            config: feConfig,
        },
    };
};

const CityLevel1 = ({ destination, popularCities, popularCountries, config }: CityPageProps) => {
    const factory = cityLevel1Factory(destination, carrentalData, carRentalSearchFactory());

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.carrental} data={carrentalData}>
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={carrentalData.cdnPath}
                    verticalUrl={carrentalData.basePath}
                    lang={carrentalData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={carrentalData.cdnPath}
                    verticalUrl={carrentalData.basePath}
                    lang={carrentalData.lang}
                />
                <LinkTree
                    heading={carrentalData.lang.linkTree.heading}
                    description={carrentalData.lang.linkTree.description}
                    data={carrentalData.linkTree}
                    url={carrentalData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel1;
